import React from 'react'
import { useNavigate } from 'react-router-dom'
import { tanstackQueryClient } from 'apps/ids/src/app/api/tanstackClient'
import {
    usePutIdsHealthcareservicesHcsIdPatientsPatientId,
    getGetIdsHealthcareservicesHcsIdPatientsPatientIdQueryKey,
    useDeleteIdsHealthcareservicesHcsIdPatientsPatientId,
    usePostIdsHealthcareservicesHcsIdPatients,
} from '@toktokdoctech/tokapi-client'
import { AxiosError } from 'axios'
import { displaySnackbar } from '../../../../actions/main'
import useCurrentHcs from '../../../../hooks/useCurrentHcs'
import useCurrentUser from '../../../../hooks/useCurrentUser'
import { handleExternal } from '../../../../routers/_props'
import { useAppDispatch } from '../../../../store'
import { usePatientFormContext } from '../../context/PatientFormContext'
import { ActionDetail, FormatedPatientFormData, WorkbenchStatus } from '../_props'
import { workbenchStatuses } from '../_utils'
import useGetIdentityProof from '../PatientIdentity/_utils/useGetIdentityProof'
import { UsePatientServicesProps } from './_props'
import usePatientUtils from './usePatientUtils'
import { useGetSavablePatientData } from './useGetSavablePatientData'
import { transformCreatePatientData, transformUpdatePatientData } from './_utils'
import { patientFormCountryBirthErrors } from '../../_errors'

const usePatientServices = ({ formValues, data, reset, getFieldState, setError }: UsePatientServicesProps) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const { checkUpdatedFields } = usePatientUtils()

    const { state, update } = usePatientFormContext()
    const currentUser = useCurrentUser()
    const { id: hcsId } = useCurrentHcs()
    const currentIdentityStatus = state.ins.identityStatus

    const updatePatientMutation = usePutIdsHealthcareservicesHcsIdPatientsPatientId()

    const { mutate: deletePatient, isPending: isDeletingPatient } =
        useDeleteIdsHealthcareservicesHcsIdPatientsPatientId({
            mutation: {
                onSuccess: () => {
                    const patientDetailKey = getGetIdsHealthcareservicesHcsIdPatientsPatientIdQueryKey(
                        hcsId,
                        data?.objectId ?? '',
                    )
                    tanstackQueryClient.removeQueries({ queryKey: patientDetailKey })

                    const patientListKey = getGetIdsHealthcareservicesHcsIdPatientsPatientIdQueryKey(hcsId)
                    tanstackQueryClient.invalidateQueries({ queryKey: patientListKey })

                    if (state.isExternal) {
                        handleExternal('cancel')
                    } else {
                        dispatch(
                            displaySnackbar(
                                'success',
                                'Fiche patient supprimée avec succès',
                                undefined,
                                `[PatientForm] suppression patient ${data?.objectId ?? ''}`,
                            ),
                        )
                        navigate('/patients')
                    }
                },
                onError: (err: unknown) => {
                    const errorMessage = err instanceof Error ? err.message : 'Unknown error'
                    console.error({ err })

                    dispatch(
                        displaySnackbar(
                            'error',
                            'Une erreur est survenue lors de la suppression de la fiche patient',
                            undefined,
                            `[PatientForm] suppression patient ${data?.objectId ?? ''} error: ${errorMessage}`,
                        ),
                    )
                },
            },
        })

    const createPatientMutation = usePostIdsHealthcareservicesHcsIdPatients()

    const { getIdentityProof, loading: loadingIdentityProofs, error: errorIdentityProofs } = useGetIdentityProof()

    const getSavablePatientFormData = useGetSavablePatientData()

    // specific case for patient not born in France (department code 99)
    const handleSsnApiError = (e: unknown) => {
        if (e instanceof AxiosError) {
            const isForeignBirthCountryError = e.response?.data?.errors?.SocialSecurityNumber?.find((er: string) =>
                er.includes('code 99'),
            )
            if (isForeignBirthCountryError) {
                setError('socialSecurityNumber', {
                    type: 'manual',
                    message: patientFormCountryBirthErrors.ssnMismatched,
                })
            } else {
                setError('root', {
                    message: 'Une erreur est survenue',
                })
            }
        }
        throw e
    }

    const isSaving = React.useMemo(
        () => updatePatientMutation.isPending || createPatientMutation.isPending || loadingIdentityProofs,
        [createPatientMutation.isPending, updatePatientMutation.isPending, loadingIdentityProofs],
    )
    const error = React.useMemo(() => errorIdentityProofs, [errorIdentityProofs])

    const handleUpdatePatient = async (submitValues: FormatedPatientFormData, workbenchStatus?: WorkbenchStatus) => {
        const hasInsConflicts = workbenchStatus ? workbenchStatuses.conflict.includes(workbenchStatus) : false

        const noIdentityProof = getIdentityProof(1)

        const computedSavableValues: FormatedPatientFormData = {
            ...submitValues,
            identityProof: hasInsConflicts ? noIdentityProof : submitValues.identityProof,
            insOid: hasInsConflicts ? '' : submitValues.insOid,
            insNum: hasInsConflicts ? '' : submitValues.insNum,
        }

        if (hasInsConflicts) {
            computedSavableValues.lastInsiIdentityCheckResult = 'CONFLICT'
        } else if (hasInsConflicts === false) {
            computedSavableValues.lastInsiIdentityCheckResult = 'SUCCESS'
        }

        let updatedFields: ActionDetail[] = []
        if (data) {
            updatedFields = await checkUpdatedFields(data, submitValues, getFieldState)
        }

        const savableValues = await getSavablePatientFormData({
            data: computedSavableValues,
            mode: state.mode,
            currentUser,
            updatedFields,
        })

        try {
            const data = transformUpdatePatientData(savableValues, submitValues, currentIdentityStatus)
            const updatedPatient = await updatePatientMutation.mutateAsync({
                hcsId,
                patientId: formValues.objectId,
                data,
            })

            reset({
                ...computedSavableValues,
                ...updatedPatient,
            })
            if (!hasInsConflicts) {
                dispatch(
                    displaySnackbar(
                        'success',
                        'Patient modifié avec succès !',
                        undefined,
                        `[PatientForm] patient ${formValues.objectId} update`,
                    ),
                )
            } else {
                update({
                    insiResultDialogOpen: true,
                })
            }
            if (state.isExternal) {
                handleExternal('save', formValues.objectId)
            }

            const patientDetailKey = getGetIdsHealthcareservicesHcsIdPatientsPatientIdQueryKey(
                hcsId,
                formValues.objectId,
            )
            tanstackQueryClient.invalidateQueries({ queryKey: patientDetailKey })
        } catch (e) {
            console.error({ e })
            if (e instanceof AxiosError && e.response?.status === 409) {
                dispatch(
                    displaySnackbar(
                        'error',
                        'Un patient existe déjà avec ces informations',
                        undefined,
                        `[PatientForm] patient ${formValues.objectId} update error (doublon): ${e.message}`,
                    ),
                )
            } else if (e instanceof Error) {
                dispatch(
                    displaySnackbar(
                        'error',
                        'Une erreur est survenue lors de la modification du patient',
                        undefined,
                        `[PatientForm] patient ${formValues.objectId} update error: ${e.message}`,
                    ),
                )
            } else {
                dispatch(
                    displaySnackbar(
                        'error',
                        'Une erreur est survenue lors de la modification du patient',
                        undefined,
                        `[PatientForm] patient ${formValues.objectId} update unknown error`,
                    ),
                )
            }
            handleSsnApiError(e)
        }
    }

    const handleCreatePatient = async (submitValues: FormatedPatientFormData) => {
        const savableValues = await getSavablePatientFormData({
            data: submitValues,
            mode: state.mode,
            currentUser,
        })

        try {
            const patientData = transformCreatePatientData(savableValues, submitValues, currentIdentityStatus)
            const createdPatient = await createPatientMutation.mutateAsync({
                hcsId,
                data: patientData,
            })
            const patientId = createdPatient.id

            if (!patientId) {
                throw new Error("Unable to retrieve patient's id")
            }

            if (state.isExternal) {
                handleExternal('save', patientId)
            } else {
                dispatch(
                    displaySnackbar(
                        'success',
                        'Patient créé avec succès !',
                        undefined,
                        `[PatientForm] création patient ${patientId}`,
                    ),
                )
            }
        } catch (e) {
            if (e instanceof AxiosError && e.response?.status === 409) {
                dispatch(
                    displaySnackbar(
                        'error',
                        'Un patient existe déjà avec ces informations',
                        undefined,
                        `[PatientForm] erreur création patient (doublon) ${e.message}`,
                    ),
                )
            } else if (e instanceof Error) {
                dispatch(
                    displaySnackbar(
                        'error',
                        'Une erreur est survenue lors de la création du patient',
                        undefined,
                        `[PatientForm] erreur création patient ${e.message}`,
                    ),
                )
            } else {
                dispatch(
                    displaySnackbar(
                        'error',
                        'Une erreur est survenue lors de la création du patient',
                        undefined,
                        `[PatientForm] erreur innatendue lors de la création patient`,
                    ),
                )
            }
            handleSsnApiError(e)
        }
    }

    const handleDeletePatient = () => {
        deletePatient({
            hcsId,
            patientId: data?.objectId ?? '',
        })
    }

    return {
        handleUpdatePatient,
        handleCreatePatient,
        handleDeletePatient,
        isDeletingPatient,
        isSaving,
        error,
    }
}

export default usePatientServices
